import { ChevronDown, ChevronRight, Maximize2, Pin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Announcement, api, Performance, Session, Task } from "../api";
import { BlockRenderer, paragraph } from "../components/block-editor";
import { Empty, Loading, PageHead, Pagination } from "../components/ui";
import { AnnouncementViewerModal } from "../components/AnnouncementViewerModal";
import { ddayText, ddayTone, isoDate, koDate } from "../lib/format";
import type { View } from "../lib/types";
import { actionable, taskStateKey } from "./Tasks";
import { periods } from "./Performance";
import { CalendarWidget } from "./Calendar";

const NEW_NOTICE_WINDOW_MS = 7 * 24 * 60 * 60 * 1_000;
const noticeReadKey = (workspaceDomain: string) => `angelflow.notice-reads.${workspaceDomain}`;

function storedNoticeReads(workspaceDomain: string): Set<string> {
  try {
    const value = JSON.parse(localStorage.getItem(noticeReadKey(workspaceDomain)) || "{}") as Record<string, string>;
    return new Set(Object.keys(value));
  } catch {
    localStorage.removeItem(noticeReadKey(workspaceDomain));
    return new Set();
  }
}

function isNewNotice(createdAt: string) {
  const age = Date.now() - Date.parse(createdAt);
  return Number.isFinite(age) && age >= 0 && age <= NEW_NOTICE_WINDOW_MS;
}

/**
 * 첫 화면.
 *
 * 왼쪽 절반은 공지가 통째로 쓰고, 오른쪽 절반을 위아래로 갈라 해야 할 일과 성과를 둔다.
 * 데이터는 모두 기존 것을 그대로 쓴다 — 대시보드 전용 집계 엔드포인트를 두지 않았다.
 */
export function DashboardPage({ session, tasks, boardEnabled, onOpenTask, onNavigate }: {
  session: Session;
  tasks: Task[];
  boardEnabled: boolean;
  onOpenTask: (taskId: string) => void;
  onNavigate: (view: View) => void;
}) {
  const { t, i18n } = useTranslation();
  const [announcements, setAnnouncements] = useState<Announcement[] | null>(null);
  const [noticePage, setNoticePage] = useState(0);
  const [noticePages, setNoticePages] = useState(0);
  const [noticeTotal, setNoticeTotal] = useState(0);
  const [readNoticeIds, setReadNoticeIds] = useState<Set<string>>(() => storedNoticeReads(session.workspaceDomain));
  const [performance, setPerformance] = useState<Performance | null>(null);
  const [openNotice, setOpenNotice] = useState<string | null>(null);
  const [previewNotice, setPreviewNotice] = useState<Announcement | null>(null);
  const [error, setError] = useState("");

  // 성과는 성과 화면의 첫 기간(올해)을 그대로 쓴다. 여기서 기간을 고르게 하지 않는다.
  const period = useMemo(() => periods(new Date(), i18n.resolvedLanguage || i18n.language)[0], [i18n.language, i18n.resolvedLanguage]);

  function markNoticeRead(id: string) {
    setReadNoticeIds((current) => {
      if (current.has(id)) return current;
      const next = new Set(current).add(id);
      try {
        const key = noticeReadKey(session.workspaceDomain);
        const stored = JSON.parse(localStorage.getItem(key) || "{}") as Record<string, string>;
        stored[id] = new Date().toISOString();
        const recent = Object.fromEntries(Object.entries(stored)
          .sort(([, left], [, right]) => left.localeCompare(right)).slice(-500));
        localStorage.setItem(key, JSON.stringify(recent));
      } catch { /* local cache unavailable: keep the in-memory read state */ }
      return next;
    });
  }

  useEffect(() => {
    if (!boardEnabled) { setAnnouncements([]); return; }
    setAnnouncements(null);
    void api.announcements(noticePage, 5)
      .then((next) => {
        setAnnouncements(next.items);
        setNoticePages(next.totalPages);
        setNoticeTotal(next.totalElements);
        setOpenNotice(null);
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : t("dashboard.noticeLoadError")));
  }, [boardEnabled, noticePage]);

  useEffect(() => {
    void api.performance(session.userId, period.from, period.to)
      .then(setPerformance)
      .catch(() => setPerformance(null));
  }, [session.userId, period.from, period.to]);

  // 업무 화면과 같은 판정을 쓴다. 두 화면이 서로 다른 "해야 할 일"을 보이면 안 된다.
  const todo = tasks
    .filter((task) => actionable(task, session.userId))
    .sort((a, b) => (isoDate(a.dueOn) || "9999-99-99").localeCompare(isoDate(b.dueOn) || "9999-99-99"));

  return <>
    <PageHead title={t("dashboard.title")} meta={t("dashboard.periodMeta", { email: session.email, period: period.label })} />
    {error && <div className="global-error">{error}</div>}

    <div className="dashboard-grid">
      <div className="dashboard-main">
      {boardEnabled && <section className="section-card dashboard-notices">
        <header>
          <div><span className="mono-label">NOTICE</span><h2>{t("dashboard.notices")}</h2></div>
          <span className="mono-label">{announcements ? t("dashboard.count", { count: noticeTotal }) : ""}</span>
        </header>
        {!announcements ? <Loading layout="list" label={t("dashboard.loadingNotices")} />
          : announcements.length === 0
            ? <Empty title={t("dashboard.noNotices")} copy={t("dashboard.noNoticesHelp")} />
            : <ul className="notice-list">
              {announcements.map((notice) => {
                const open = openNotice === notice.id;
                const unread = !readNoticeIds.has(notice.id);
                return <li key={notice.id} className={`${open ? "open" : ""}${unread ? " is-unread" : ""}`}>
                  <div className="notice-row">
                    <button type="button" className="notice-toggle" aria-expanded={open} onClick={() => {
                      if (!open) markNoticeRead(notice.id);
                      setOpenNotice(open ? null : notice.id);
                    }}>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      {notice.pinned && <Pin size={12} className="notice-pin" aria-label={t("dashboard.pinned")} />}
                      <span className="notice-title">{notice.untrustedTitle}</span>
                      {isNewNotice(notice.publishedAt || notice.createdAt) && <span className="notice-new">NEW</span>}
                      <time className="mono">{koDate(notice.publishedAt || notice.createdAt)}</time>
                    </button>
                  </div>
                  {open && <div className="notice-body"><div className="notice-body-actions"><button type="button" className="notice-expand" aria-label={t("dashboard.expand")} title={t("dashboard.expandTitle", { title: notice.untrustedTitle })} onClick={() => setPreviewNotice(notice)}><Maximize2 size={15} /></button></div><BlockRenderer blocks={notice.blocks?.length ? notice.blocks : [paragraph(notice.untrustedBody)]} loadImage={api.downloadFile} /></div>}
                </li>;
              })}
            </ul>}
        <Pagination page={noticePage} totalPages={noticePages} onPage={setNoticePage} />
      </section>}
      <CalendarWidget session={session} tasks={tasks} onOpen={() => onNavigate("calendar")} onOpenTask={onOpenTask} />
      </div>

      <div className="dashboard-side">
        <section className="section-card">
          <header>
            <div><span className="mono-label">TODO</span><h2>{t("dashboard.todo")}</h2></div>
            <button type="button" className="link-button" onClick={() => onNavigate("tasks")}>{t("dashboard.allTasks")}</button>
          </header>
          {todo.length === 0
            ? <Empty title={t("dashboard.noTodo")} copy={t("dashboard.noTodoHelp")} />
            : <div className="notice-todo">
              {todo.slice(0, 8).map((task) => <button type="button" key={task.id} onClick={() => onOpenTask(task.id)}>
                <span><em>{t(taskStateKey(task, session.userId))}</em><small className={`mono dday ${ddayTone(task)}`}>{ddayText(task)}</small></span>
                <b>{task.internalTitle}</b>
              </button>)}
              {todo.length > 8 && <p className="permission-note">{t("dashboard.more", { count: todo.length - 8 })}</p>}
            </div>}
        </section>

        <section className="section-card">
          <header>
            <div><span className="mono-label">PERFORMANCE</span><h2>{t("dashboard.performance")}</h2></div>
            <button type="button" className="link-button" onClick={() => onNavigate("performance")}>{t("dashboard.details")}</button>
          </header>
          {!performance ? <Loading layout="cards" label={t("dashboard.loadingPerformance")} /> : <div className="stat-row">
            <div className="stat"><div className="mono-label">{t("dashboard.registeredTasks")}</div><strong>{performance.taskCount}</strong><small>{period.label}</small></div>
            <div className="stat"><div className="mono-label">{t("dashboard.quality")}</div><strong>{performance.averageQualityScore === null || performance.averageQualityScore === undefined ? "-" : `${performance.averageQualityScore.toFixed(1)} / 5`}</strong><small>{t("dashboard.qualityHelp")}</small></div>
            <div className="stat"><div className="mono-label">{t("dashboard.onTime")}</div><strong>{performance.onTimeRate === null || performance.onTimeRate === undefined ? "-" : `${Math.round(performance.onTimeRate)}%`}</strong><small>{t("dashboard.onTimeHelp")}</small></div>
          </div>}
        </section>
      </div>
    </div>
    {previewNotice && <AnnouncementViewerModal announcement={previewNotice} onClose={() => setPreviewNotice(null)} />}
  </>;
}
